<template>
    <div class="LargeFormatIdScanForm">
        <div class="LargeFormatIdScanForm__delegation">
            <h2 class="LargeFormatIdScanForm__title">
                {{ delegationTitle }}
            </h2>
            <p class="LargeFormatIdScanForm__subtitle">
                Scan this QR code with your phone's camera to capture your ID there.
            </p>

            <div
                v-if="delegationStatus === 'error'"
                class="LargeFormatIdScanForm__error"
                role="status"
                data-test-id="IncodeDelegationForm__error"
            >
                <h3><Icon name="alert" /> We couldn't build your QR code</h3>
                <p>
                    Try again in a few minutes, or open the link in your verification email
                    directly on a mobile device.
                </p>
                <button
                    class="LargeFormatIdScanForm__retry"
                    type="button"
                    data-test-id="IncodeDelegationForm__retry"
                    @click="requestLink"
                >
                    Try again
                </button>
            </div>

            <template v-else-if="delegationUrl">
                <QrCode
                    class="LargeFormatIdScanForm__qr"
                    :content="delegationUrl"
                    @render-error="handleCodeRenderError"
                />
                <div class="LargeFormatIdScanForm__urlWrapper">
                    <span class="LargeFormatIdScanForm__urlLabel">{{ urlLabel }}</span>
                    <span
                        class="LargeFormatIdScanForm__url"
                        data-test-id="IncodeDelegationForm__url"
                    ><var>{{ delegationUrl }}</var></span>
                </div>

                <div class="LargeFormatIdScanForm__notice">
                    <h3><Icon name="alert" /> Waiting for your upload</h3>
                    <p>
                        You can stay on this page and continue when your upload is complete,
                        or finish verification on your mobile device.
                    </p>
                </div>
            </template>

            <p
                v-else
                class="LargeFormatIdScanForm__pending"
                role="status"
                data-test-id="IncodeDelegationForm__pending"
            >
                Generating your QR code…
            </p>
        </div>
        <QuestionFormControls :has-submit="false" @back="onBack" />
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Watch } from 'vue-property-decorator';

    import Icon from '@/components/common/Icon.vue';
    import QrCode from '@/components/common/QrCode.vue';
    import QuestionFormControls from '@/components/forms/QuestionFormControls.vue';
    import FieldComponent from '@/fields/FieldComponent';

    // Refresh this long before the link expires rather than at it, so a scan started just
    // before the boundary still lands on a live link.
    const REFRESH_MARGIN_MS = 60 * 1000;
    // Floor on the delay, so a short or already-elapsed TTL cannot turn into a request loop.
    const MIN_REFRESH_MS = 30 * 1000;

    @Component({
        emits: [ 'back', 'delegate' ],
        inheritAttrs: false,
        components: { QrCode, QuestionFormControls, Icon },
    })
    export default class IncodeDelegationForm extends FieldComponent<any> {
        @Prop({ type: String, default: 'Use your mobile phone to complete your identity verification' })
        private delegationTitle!: string;

        @Prop({ type: String, default: null })
        private delegationUrl!: string | null;

        @Prop({ type: Number, default: null })
        private delegationExpiresAt!: number | null;

        // Set when the code could not be drawn. The link below is the whole fallback, so the
        // label stops referring to a code the user cannot see.
        private codeUnavailable: boolean = false;

        private refreshTimer: number | null = null;

        private handleCodeRenderError() {
            this.codeUnavailable = true;
        }

        // A new URL gets its own attempt at drawing, so a failure on the previous one must
        // not leave the fallback label showing above a code that is now on screen.
        @Watch('delegationUrl')
        private onDelegationUrlChange() {
            this.codeUnavailable = false;
        }

        private get urlLabel(): string {
            return this.codeUnavailable
                ? 'Open this link on your phone to continue:'
                : 'Or open this link on your phone:';
        }

        private mounted() {
            this.requestLink();
            this.scheduleRefresh();
        }

        private unmounted() {
            this.clearRefresh();
        }

        // The link carries a server-side TTL measured in minutes, and this screen's whole
        // premise is that the user leaves it open while they work on their phone. Without a
        // refresh the desktop keeps painting a healthy-looking code that resolves to "Link not
        // found" on the phone, and the only recovery is a page reload the user has no reason
        // to attempt. Refreshing rather than erroring out keeps that invisible: `requestLink`
        // is deterministic for the session, so it returns the same URL with a fresh TTL and
        // the code on screen never changes.
        @Watch('delegationExpiresAt')
        private onDelegationExpiryChange() {
            this.scheduleRefresh();
        }

        private scheduleRefresh() {
            this.clearRefresh();

            if (!this.delegationExpiresAt) {
                return;
            }

            const msUntilRefresh = this.delegationExpiresAt * 1000 - Date.now() - REFRESH_MARGIN_MS;

            // Floored, so an already-elapsed or very short TTL cannot become a request loop.
            this.refreshTimer = window.setTimeout(
                () => this.requestLink(),
                Math.max(msUntilRefresh, MIN_REFRESH_MS),
            );
        }

        private clearRefresh() {
            if (this.refreshTimer !== null) {
                window.clearTimeout(this.refreshTimer);
                this.refreshTimer = null;
            }
        }

        private requestLink() {
            this.$emit('delegate');
        }

        private onBack() {
            this.$emit('back');
        }
    }
</script>

<style lang="less">
    .LargeFormatIdScanForm {
        &__qr {
            margin: 1.5rem 0 1rem;
        }

        &__urlWrapper {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.25rem;
            margin-bottom: 1.5rem;
            text-align: center;
        }

        &__urlLabel {
            font-size: 0.875rem;
        }

        // A hashed token has no word boundaries, so without an explicit rule it overflows
        // its container. `anywhere` breaks only when the line cannot otherwise fit.
        &__url {
            font-size: 0.875rem;
            font-weight: bold;
            overflow-wrap: anywhere;
            user-select: all;
        }

        &__pending {
            margin: 2rem 0;
            text-align: center;
        }
    }
</style>
